import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Flex,
  HStack,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  SimpleGrid,
  Spinner,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  VStack,
} from '@chakra-ui/react';
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { useI18n } from '~/i18n';
import { CHART_FONT_FAMILY } from '~/styles/chart';

export type MatrixGenerationValues = {
  evapMin: string;
  evapStep: string;
  evapMax: string;
  condMin: string;
  condStep: string;
  condMax: string;
  subcooling: string;
  superheat: string;
};

export type MatrixRow = {
  tempEvaporacao: number;
  tempCondensacao: number;
  capacidadeRefrigeracao: number;
  potenciaConsumida: number;
  cop: number;
  calorRejeitado: number;
  corrente: number;
  temperaturaDescarga: number | null;
};

type Props = {
  isOpen: boolean;
  onClose: () => void;
  tempUnit: 'c' | 'f';
  capacitySuffix: string;
  normativeLabel: string;
  initialValues: MatrixGenerationValues;
  rows: MatrixRow[];
  isLoading: boolean;
  onGenerate: (values: MatrixGenerationValues, mode?: 'standard' | 'en12900') => Promise<void> | void;
};

type MatrixChartKey =
  | 'capacidadeRefrigeracao'
  | 'potenciaConsumida'
  | 'cop'
  | 'calorRejeitado';

type MatrixChartConfig = {
  key: MatrixChartKey;
  title: string;
  yLabel: string;
};

const celsiusToFahrenheit = (value: number) => (value * 9) / 5 + 32;

const formatNumber = (value: number | null | undefined, digits?: number) => {
  const precision = typeof digits === 'number' ? digits : 2;

  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return '-';
  }

  return value.toFixed(precision);
};

const defaultRangesByUnit = (tempUnit: 'c' | 'f') => {
  if (tempUnit === 'f') {
    return {
      evapMin: celsiusToFahrenheit(-45).toFixed(1),
      evapStep: '9.0',
      evapMax: celsiusToFahrenheit(15).toFixed(1),
      condMin: celsiusToFahrenheit(20).toFixed(1),
      condStep: '9.0',
      condMax: celsiusToFahrenheit(60).toFixed(1),
    };
  }

  return {
    evapMin: '-45.0',
    evapStep: '5.0',
    evapMax: '15.0',
    condMin: '20.0',
    condStep: '5.0',
    condMax: '60.0',
  };
};

const excelEscape = (value: string) =>
  String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

export function MatrixWindow({
  isOpen,
  onClose,
  tempUnit,
  capacitySuffix,
  normativeLabel,
  initialValues,
  rows,
  isLoading,
  onGenerate,
}: Props) {
  const [form, setForm] = useState(initialValues);
  const [isGraphOpen, setIsGraphOpen] = useState(false);
  const { t } = useI18n();

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    setForm({
      ...defaultRangesByUnit(tempUnit),
      subcooling: initialValues.subcooling,
      superheat: initialValues.superheat,
    });
  }, [initialValues, isOpen, tempUnit]);

  const unitLabel = tempUnit === 'f' ? '°F' : '°C';

  const columns = useMemo(
    () => [
      `T. Evap. (${unitLabel})`,
      `T. Cond. (${unitLabel})`,
      `Capacidade (${capacitySuffix})`,
      'Potência (kW)',
      'COP',
      `Calor Rejeitado (${capacitySuffix})`,
      'Corrente (A)',
      `T. Descarga (${unitLabel})`,
    ],
    [capacitySuffix, unitLabel]
  );

  const updateField = (field: string, value: string) => {
    setForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const exportMatrixExcel = () => {
    if (!rows.length) {
      return;
    }

    const tableRows = rows
      .map((row) => [
        formatNumber(row.tempEvaporacao, 1),
        formatNumber(row.tempCondensacao, 1),
        formatNumber(row.capacidadeRefrigeracao, 2),
        formatNumber(row.potenciaConsumida, 2),
        formatNumber(row.cop, 2),
        formatNumber(row.calorRejeitado, 2),
        formatNumber(row.corrente, 2),
        formatNumber(row.temperaturaDescarga, 1),
      ])
      .map((values) => `<tr>${values.map((value) => `<td>${excelEscape(value)}</td>`).join('')}</tr>`)
      .join('');

    const html = `
      <html xmlns:o="urn:schemas-microsoft-com:office:office"
            xmlns:x="urn:schemas-microsoft-com:office:excel"
            xmlns="http://www.w3.org/TR/REC-html40">
        <head>
          <meta charset="utf-8" />
          <meta http-equiv="content-type" content="application/vnd.ms-excel; charset=UTF-8" />
        </head>
        <body>
          <table>
            <thead>
              <tr>${columns.map((column) => `<th>${excelEscape(column)}</th>`).join('')}</tr>
            </thead>
            <tbody>${tableRows}</tbody>
          </table>
        </body>
      </html>
    `;

    const blob = new Blob([html], {
      type: 'application/vnd.ms-excel;charset=utf-8;',
    });

    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'matriz_compressores.xls';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const graphConfig = useMemo(
    (): MatrixChartConfig[] => [
      {
        key: 'capacidadeRefrigeracao',
        title: t('Capacidade'),
        yLabel: `{${t('Capacidade')} (${capacitySuffix})}`,
      },
      {
        key: 'potenciaConsumida',
        title: t('Potência Consumida'),
        yLabel: `${t('Potência')} (kW)`,
      },
      {
        key: 'cop',
        title: t('COP'),
        yLabel: t('COP'),
      },
      {
        key: 'calorRejeitado',
        title: t('Calor Rejeitado'),
        yLabel: `{${t('Calor Rejeitado')} (${capacitySuffix})}`,
      },
    ],
    [capacitySuffix]
  );

  const condSeries = useMemo(() => {
    const grouped = new Map<string, { condValue: number; points: MatrixRow[] }>();

    rows.forEach((row) => {
      const key = row.tempCondensacao.toFixed(3);
      const current = grouped.get(key);

      if (current) {
        current.points.push(row);
      } else {
        grouped.set(key, {
          condValue: row.tempCondensacao,
          points: [row],
        });
      }
    });

    return Array.from(grouped.values())
      .map((item) => ({
        condValue: item.condValue,
        points: item.points.slice().sort((a, b) => a.tempEvaporacao - b.tempEvaporacao),
      }))
      .sort((a, b) => a.condValue - b.condValue);
  }, [rows]);

  const chartPalette = ['#1a365d', '#2b6cb0', '#2f855a', '#b7791f', '#c53030', '#6b46c1', '#0f766e', '#dd6b20'];

  const subcoolingSuffix = tempUnit === 'f' ? '°F' : 'K';
  const superheatSuffix = tempUnit === 'f' ? '°F' : 'K';

  let tableContent = (
    <Flex align="center" justify="center" py={12}>
      <Text color="gray.500">{t('Nenhuma linha gerada ainda.')}</Text>
    </Flex>
  );

  if (isLoading) {
    tableContent = (
      <Flex align="center" justify="center" py={16} direction="column">
        <Spinner
          thickness="4px"
          speed="0.65s"
          emptyColor="gray.200"
          color="blue.500"
          size="xl"
        />
        <Text mt={3}>{t('Gerando matriz...')}</Text>
      </Flex>
    );
  } else if (rows.length > 0) {
    tableContent = (
      <Table size="sm" variant="striped">
        <Thead position="sticky" top={0} bg="white" zIndex={1}>
          <Tr>
            {columns.map((column) => (
              <Th key={column}>{column}</Th>
            ))}
          </Tr>
        </Thead>
        <Tbody>
          {rows.map((row, index) => (
            <Tr key={`${row.tempEvaporacao}-${row.tempCondensacao}-${String(index)}`}>
              <Td>{formatNumber(row.tempEvaporacao, 1)}</Td>
              <Td>{formatNumber(row.tempCondensacao, 1)}</Td>
              <Td>{formatNumber(row.capacidadeRefrigeracao, 2)}</Td>
              <Td>{formatNumber(row.potenciaConsumida, 2)}</Td>
              <Td>{formatNumber(row.cop, 2)}</Td>
              <Td>{formatNumber(row.calorRejeitado, 2)}</Td>
              <Td>{formatNumber(row.corrente, 2)}</Td>
              <Td>{formatNumber(row.temperaturaDescarga, 1)}</Td>
            </Tr>
          ))}
        </Tbody>
      </Table>
    );
  }

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} size="6xl" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{t('Matriz de Resultados')}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack align="stretch" spacing={4}>
              <Box borderWidth="1px" borderRadius="md" p={4}>
                <VStack align="stretch" spacing={3}>
                  <Text fontWeight="700">{t('Faixas de temperatura')}</Text>

                  <Box overflowX="auto">
                    <Table size="sm" variant="simple">
                      <Thead>
                        <Tr>
                          <Th />
                          <Th>MIN</Th>
                          <Th>PASSO</Th>
                          <Th>MAX</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        <Tr>
                          <Td fontWeight="700">{t('Evaporação')} ({unitLabel})</Td>
                          <Td>
                            <Input
                              value={form.evapMin}
                              onChange={(e) => updateField('evapMin', e.target.value)}
                            />
                          </Td>
                          <Td>
                            <Input
                              value={form.evapStep}
                              onChange={(e) => updateField('evapStep', e.target.value)}
                            />
                          </Td>
                          <Td>
                            <Input
                              value={form.evapMax}
                              onChange={(e) => updateField('evapMax', e.target.value)}
                            />
                          </Td>
                        </Tr>

                        <Tr>
                          <Td fontWeight="700">{t('Condensação')} ({unitLabel})</Td>
                          <Td>
                            <Input
                              value={form.condMin}
                              onChange={(e) => updateField('condMin', e.target.value)}
                            />
                          </Td>
                          <Td>
                            <Input
                              value={form.condStep}
                              onChange={(e) => updateField('condStep', e.target.value)}
                            />
                          </Td>
                          <Td>
                            <Input
                              value={form.condMax}
                              onChange={(e) => updateField('condMax', e.target.value)}
                            />
                          </Td>
                        </Tr>
                      </Tbody>
                    </Table>
                  </Box>

                  <HStack spacing={4} align="end" flexWrap="wrap">
                    <Box minW="220px">
                      <Text fontWeight="700" mb={1}>
                        {t('Sub-resfriamento')} ({subcoolingSuffix})
                      </Text>
                      <Input
                        value={form.subcooling}
                        onChange={(e) => updateField('subcooling', e.target.value)}
                      />
                    </Box>

                    <Box minW="220px">
                      <Text fontWeight="700" mb={1}>
                        {t('Superaquecimento total')} ({superheatSuffix})
                      </Text>
                      <Input
                        value={form.superheat}
                        onChange={(e) => updateField('superheat', e.target.value)}
                      />
                    </Box>

                    <HStack spacing={3} flexWrap="wrap">
                      <Button
                        colorScheme="blue"
                        onClick={() => onGenerate(form, 'standard')}
                        isLoading={isLoading}
                      >
                        {t('Gerar Matriz')}
                      </Button>

                      <Button
                        variant="outline"
                        onClick={() => onGenerate(form, 'en12900')}
                        isLoading={isLoading}
                      >
                        {t('Gerar Matriz')} ({t(normativeLabel)})
                      </Button>

                      <Button
                        variant="outline"
                        onClick={() => setIsGraphOpen(true)}
                        isDisabled={isLoading || rows.length === 0}
                      >
                        {t('Plotar Gráficos')}
                      </Button>

                      <Button
                        variant="outline"
                        onClick={exportMatrixExcel}
                        isDisabled={isLoading || rows.length === 0}
                      >
                        {t('Exportar Excel')}
                      </Button>
                    </HStack>
                  </HStack>
                </VStack>
              </Box>

              <Box borderWidth="1px" borderRadius="md" p={2} overflow="auto" maxH="420px">
                {tableContent}
              </Box>
            </VStack>
          </ModalBody>

          <ModalFooter>
            <Button onClick={onClose}>{t('Fechar')}</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal isOpen={isGraphOpen} onClose={() => setIsGraphOpen(false)} size="7xl" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{t('Gráficos de Performance da Matriz')}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={4} width="100%">
              {graphConfig.map((config) => (
                <Box
                  key={config.key}
                  borderWidth="1px"
                  borderRadius="md"
                  p={4}
                  bg="white"
                  fontFamily={CHART_FONT_FAMILY}
                >
                  <Text fontWeight="700" mb={4}>
                    {config.title}
                  </Text>
                  <Box h="300px">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart margin={{ top: 10, right: 30, left: 10, bottom: 10 }}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis
                          dataKey="tempEvaporacao"
                          type="number"
                          domain={['auto', 'auto']}
                          label={{
                            value: `T. Evaporação (${unitLabel})`,
                            position: 'insideBottom',
                            offset: -5,
                          }}
                        />
                        <YAxis
                          label={{
                            value: config.yLabel,
                            angle: -90,
                            position: 'insideLeft',
                          }}
                        />
                        <Tooltip
                          formatter={(value: number) => formatNumber(value, 2)}
                          labelFormatter={(value) => `T. Evap.: ${formatNumber(Number(value), 1)} ${unitLabel}`}
                        />
                        <Legend />
                        {condSeries.map((series, index) => (
                          <Line
                            key={`${config.key}-${series.condValue.toFixed(3)}`}
                            type="monotone"
                            data={series.points}
                            dataKey={config.key}
                            name={`T. Cond = ${formatNumber(series.condValue, 1)} ${unitLabel}`}
                            stroke={chartPalette[index % chartPalette.length]}
                            strokeWidth={2}
                            dot={{ r: 3 }}
                            isAnimationActive={false}
                          />
                        ))}
                      </LineChart>
                    </ResponsiveContainer>
                  </Box>
                </Box>
              ))}
            </SimpleGrid>
          </ModalBody>
          <ModalFooter>
            <Button onClick={() => setIsGraphOpen(false)}>{t('Fechar')}</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
}

export default MatrixWindow;
